import { Capacitor, registerPlugin } from '@capacitor/core';

// The RevenueCat native plugin, reached through Capacitor's bridge under
// the name '@revenuecat/purchases-capacitor' itself registers ('Purchases').
// Not importing that package's JS keeps this file building where the
// package isn't installed (Emergent's web build); the native code still
// ships in the apps from package.json via `cap sync`. Every call below is
// gated by apiKey(), which is empty on the web, so the missing web
// implementation is never hit.
const Purchases = registerPlugin('Purchases');

// Public SDK keys — safe to ship in the client, same trust level as a
// Stripe publishable key. Each comes from RevenueCat → Project settings →
// API keys, one per store app.
const REVENUECAT_API_KEY_IOS = 'appl_DMPXTJfyrWotyWCefIszuUeAbty';
// Google Play key ("goog_…"). Empty: in-app purchase stays off on Android
// and the subscription page says so instead of offering a broken purchase.
const REVENUECAT_API_KEY_ANDROID = '';

const PLAY_PACKAGE_NAME = 'app.tofbox.mobile';

let configured = false;

function apiKey() {
  if (!Capacitor.isNativePlatform()) return '';
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return REVENUECAT_API_KEY_IOS;
  if (platform === 'android') return REVENUECAT_API_KEY_ANDROID;
  return '';
}

/** 'apple' | 'google' | null — the store that sells subscriptions in this
 *  app, or null on the web and on a native build with no store key. */
export function inAppStore() {
  if (!apiKey()) return null;
  return Capacitor.getPlatform() === 'ios' ? 'apple' : 'google';
}

/** Configure the SDK once per app process, identifying the subscriber as
 *  our own `user_id` from day one (no anonymous→known alias step needed).
 *  Safe to call again after login with a different id — it switches
 *  identity via `logIn` instead of re-configuring. No-ops off native. */
export async function initPurchases(userId) {
  const key = apiKey();
  if (!key) return;
  if (!configured) {
    await Purchases.configure({ apiKey: key, appUserID: userId || undefined });
    configured = true;
    if (process.env.NODE_ENV !== 'production') {
      await Purchases.setLogLevel({ level: 'DEBUG' }); // LOG_LEVEL.DEBUG
    }
  } else if (userId) {
    await Purchases.logIn({ appUserID: userId });
  }
}

/** Call on logout so a shared device doesn't keep the previous account's
 *  entitlements cached under the next anonymous session. */
export async function logOutPurchases() {
  if (!apiKey() || !configured) return;
  try { await Purchases.logOut(); } catch { /* already logged out / anonymous */ }
}

/** Returns the current Offering's packages, or [] if unavailable
 *  (misconfiguration, no network, nothing published yet). */
export async function fetchOfferingPackages() {
  if (!apiKey()) return [];
  try {
    const offerings = await Purchases.getOfferings();
    return offerings?.current?.availablePackages || [];
  } catch {
    return [];
  }
}

/** Runs the native purchase sheet for a given RevenueCat Package.
 *  Throws on failure; callers should distinguish user cancellation
 *  (`error.userCancelled === true`) from real errors. */
export async function purchasePackage(rcPackage) {
  const { customerInfo } = await Purchases.purchasePackage({ aPackage: rcPackage });
  return customerInfo;
}

export async function restorePurchases() {
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

export function activeEntitlementIds(customerInfo) {
  return Object.keys(customerInfo?.entitlements?.active || {});
}

/** Opens the store's own subscription management screen — the only
 *  compliant way to let a user change/cancel a store subscription.
 *  `store`: 'apple' | 'google', defaults to this device's store. */
export async function openManageSubscriptions(store = inAppStore()) {
  if (store === 'google') {
    window.open(`https://play.google.com/store/account/subscriptions?package=${PLAY_PACKAGE_NAME}`, '_blank', 'noopener');
    return;
  }
  if (Capacitor.getPlatform() === 'ios' && configured) {
    try {
      await Purchases.showManageSubscriptions();
      return;
    } catch { /* fall through to the web page */ }
  }
  window.open('https://apps.apple.com/account/subscriptions', '_blank', 'noopener');
}
